import { useState, forwardRef, useImperativeHandle } from 'react';
import { useProjectTrips } from '../hooks/useProjectTrips';
import type { Trip } from '../types';

export interface ProjectTripsSectionRef {
  refetch: () => Promise<void>;
}

interface ProjectTripsSectionProps {
  projectId: string;
}

export const ProjectTripsSection = forwardRef<ProjectTripsSectionRef, ProjectTripsSectionProps>(
  ({ projectId }, ref) => {
    const { trips, isLoading, error, unassignTrip, refetch } = useProjectTrips(projectId);
    const [processingIds, setProcessingIds] = useState<Set<string>>(new Set());

    // Expose refetch to parent
    useImperativeHandle(ref, () => ({
      refetch,
    }));

    const handleUnassign = async (tripId: string) => {
      setProcessingIds((prev) => new Set(prev).add(tripId));
      try {
        await unassignTrip(tripId);
      } catch (err) {
        console.error('Error unassigning trip:', err);
      } finally {
        setProcessingIds((prev) => {
          const next = new Set(prev);
          next.delete(tripId);
          return next;
        });
      }
    };

    if (isLoading) {
      return (
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600" />
        </div>
      );
    }

    if (error) {
      return (
        <div className="bg-red-50 text-red-600 p-4 rounded-lg text-sm">
          <p>{error}</p>
          <button onClick={refetch} className="mt-2 underline hover:no-underline">
            Try again
          </button>
        </div>
      );
    }

    if (trips.length === 0) {
      return (
        <div className="text-center py-8 bg-secondary-50 rounded-lg">
          <svg
            className="w-10 h-10 mx-auto text-secondary-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7"
            />
          </svg>
          <p className="mt-2 text-secondary-600">No trips assigned to this project</p>
          <p className="text-sm text-secondary-500">
            Assign trips from the Trips page
          </p>
        </div>
      );
    }

    // Calculate totals
    const totalDistance = trips.reduce((sum, t) => {
      const rounded = t.distanceKm !== null ? Math.round(t.distanceKm * 10) / 10 : 0;
      return sum + rounded;
    }, 0);
    const billableTrips = trips.filter((t) => t.billable);

    return (
      <div className="space-y-4">
        {/* Summary */}
        <div className="flex flex-wrap gap-4 text-sm">
          <div className="flex items-center gap-2 text-secondary-600">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7"
              />
            </svg>
            <span>
              {trips.length} trip{trips.length !== 1 ? 's' : ''}
            </span>
          </div>
          <div className="flex items-center gap-2 text-secondary-600">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M13 7l5 5m0 0l-5 5m5-5H6"
              />
            </svg>
            <span>{totalDistance.toFixed(1)} km total</span>
          </div>
          {billableTrips.length < trips.length && (
            <div className="flex items-center gap-2 text-secondary-500">
              <span>({billableTrips.length} billable)</span>
            </div>
          )}
        </div>

        {/* Trips List */}
        <div className="space-y-3">
          {trips.map((trip) => (
            <ProjectTripCard
              key={trip.id}
              trip={trip}
              onUnassign={() => handleUnassign(trip.id)}
              isProcessing={processingIds.has(trip.id)}
            />
          ))}
        </div>
      </div>
    );
  }
);

ProjectTripsSection.displayName = 'ProjectTripsSection';

// Internal component for displaying a trip in the project context
interface ProjectTripCardProps {
  trip: Trip;
  onUnassign: () => void;
  isProcessing: boolean;
}

function ProjectTripCard({ trip, onUnassign, isProcessing }: ProjectTripCardProps) {
  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-CA', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatTime = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-CA', {
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  const formatDistance = (km: number | null): string => {
    if (km === null || km === undefined) return '—';
    return `${km.toFixed(1)} km`;
  };

  return (
    <div className="bg-secondary-50 rounded-lg p-4">
      {/* Header Row */}
      <div className="flex items-start justify-between gap-4 mb-3">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
          <span className="font-medium text-secondary-900">
            {formatDate(trip.startTime)}
          </span>
          <span className="text-secondary-500">{formatTime(trip.startTime)}</span>
          <span className="text-secondary-400">•</span>
          <span className="text-secondary-600">{formatDistance(trip.distanceKm)}</span>
          {!trip.billable && (
            <span className="px-1.5 py-0.5 text-xs bg-secondary-200 text-secondary-600 rounded">
              Non-billable
            </span>
          )}
        </div>

        <button
          onClick={onUnassign}
          disabled={isProcessing}
          className="text-sm text-secondary-500 hover:text-red-600 disabled:opacity-50 transition-colors flex-shrink-0"
          title="Remove from project"
        >
          {isProcessing ? (
            <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
          ) : (
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          )}
        </button>
      </div>

      {/* Route Display - Vertical Stack */}
      <div className="space-y-2 text-sm">
        <div className="flex items-start gap-2">
          <div className="flex items-center gap-2 w-14 flex-shrink-0">
            <div className="w-2 h-2 bg-green-500 rounded-full" />
            <span className="text-secondary-500 font-medium">Start:</span>
          </div>
          <span className="text-secondary-700" title={trip.startAddressText || undefined}>
            {trip.startAddressText || 'Unknown location'}
          </span>
        </div>
        <div className="flex items-start gap-2">
          <div className="flex items-center gap-2 w-14 flex-shrink-0">
            <div className="w-2 h-2 bg-red-500 rounded-full" />
            <span className="text-secondary-500 font-medium">End:</span>
          </div>
          <span className="text-secondary-700" title={trip.endAddressText || undefined}>
            {trip.endAddressText || 'Unknown location'}
          </span>
        </div>
      </div>

      {/* Driver (if available) */}
      {trip.driverName && (
        <div className="mt-3 flex items-center gap-1.5 text-xs text-secondary-500">
          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
            />
          </svg>
          <span>{trip.driverName}</span>
        </div>
      )}
    </div>
  );
}